import React, { useState, useMemo, useCallback } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Card from '@mui/material/Card';
import { useWindowSize } from 'react-use';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import IconButton from '@mui/material/IconButton';

import Typography from 'src/components/ui/Typography';
import Icon from 'src/components/ui/Icon';
import TextField from 'src/components/ui/TextField';
import Button from 'src/components/ui/Button';
import HiddenPhoneNumberButton from 'src/components/ui/HiddenPhoneNumberButton';
import UserOrCompanyPreviewCore from 'src/components/chat/UserOrCompanyPreviewCore';

import useAlerts from 'src/hooks/useAlerts';
import useUser from 'src/hooks/useUser';
import useAppRouter from 'src/hooks/useAppRouter';
import useLoginModal from 'src/hooks/useLoginModal';

import type { AdQuery, Company, Owner } from 'src/types/__generated__/graphql';
import { DataType } from 'src/types/__generated__/graphql';
import {
    useIncrementStatsContactCountMutation,
    useSendSharedContentMessageMutation,
} from 'src/hooks/__generated__/queries';
import { routePathContacts, routePathUser } from 'src/constants/router';
import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import { createUsersChannel } from 'src/utils/sendbirdUtils';
import { styledComponent } from 'src/utils/styled';
import { AnalyticsEvent } from 'src/utils/analytics';
import getOwnerLink from 'src/utils/getOwnerLink';
import useModalOpenState from 'src/hooks/useModalOpenState';
import PublishersModal from 'src/components/ad/PublishersModal';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

interface StyledContactBlockProps {
    $isDesktop: boolean;
}

const StyledContactBlock = styledComponent(Card)<StyledContactBlockProps>`
    padding: 1.5rem;
    border-radius: 1rem;
    border: 1px ${colours.black.O08} solid;
    ${({ $isDesktop }) =>
        $isDesktop
            ? `
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
        align-items: stretch;
    `
            : `
        max-width: 25.25rem;
        width: 100%;
    `}
`;
StyledContactBlock.defaultProps = {
    elevation: 0,
};

const Title = styledComponent(Typography)`
    color: ${colours.primary.main};
    font-size: 1.25rem;
`;

interface FormValues {
    content: string;
}

interface Props {
    ad: NonNullable<AdQuery['ad']>;
}

const ContactBlock: React.FC<Props> = ({ ad }) => {
    const { t } = useTranslation(['common', 'ad']);
    const [sent, setSent] = useState<boolean>(false);
    const currentUser = useUser();
    const { addAlert } = useAlerts();
    const { logEventDynamic } = useDynamicLogEvents();
    const router = useAppRouter();
    const { width } = useWindowSize();
    const { createAuthenticatedHandler } = useLoginModal();
    const [incrementStatsContactCount] = useIncrementStatsContactCountMutation();
    const [sendSharedContentMessageMutation] = useSendSharedContentMessageMutation();
    const { props: publishersModalProps, open: openPublishersModal } = useModalOpenState();

    const isDesktop = useMemo(() => width > breakpoints.desktopSmall, [width]);

    const owner = ad.owner;
    const schema = Yup.object().shape({
        content: Yup.string()
            .required(t('common:errors.required'))
            .min(3, t('common:errors.tooShort', { number: 3 }))
            .max(5000, t('common:errors.tooLong', { number: 5000 })),
    });

    const sendMessage = useCallback(
        async (message: string) => {
            if (currentUser?.sendbirdUserId) {
                const sendbirdTargetId =
                    ad.assignedTo?.sendbirdUserId ??
                    owner?.company?.contactUser?.sendbirdUserId ??
                    owner?.user?.sendbirdUserId;
                if (sendbirdTargetId) {
                    const channel = await createUsersChannel(currentUser.sendbirdUserId, [sendbirdTargetId]);
                    await sendSharedContentMessageMutation({
                        variables: {
                            channelUrl: channel.url,
                            message,
                            data: {
                                type: DataType.Ad,
                                payload: {
                                    id: ad.id,
                                },
                            },
                        },
                    });
                }
            }
        },
        [
            ad.assignedTo?.sendbirdUserId,
            ad.id,
            currentUser?.sendbirdUserId,
            owner?.company?.contactUser?.sendbirdUserId,
            owner?.user?.sendbirdUserId,
            sendSharedContentMessageMutation,
        ],
    );

    const handleForm = useCallback(
        async (values: FormValues) => {
            try {
                if (currentUser) {
                    await sendMessage(values.content);
                    await incrementStatsContactCount({
                        variables: {
                            statsId: ad.stats.id as string,
                        },
                    });
                }
                addAlert({
                    type: 'success',
                    message: t('ad:messageSent'),
                });
                setSent(true);
            } catch (error: unknown) {
                addAlert({
                    type: 'error',
                    message: (error as Error).message,
                });
            }
        },
        [ad.stats.id, addAlert, currentUser, incrementStatsContactCount, sendMessage, t],
    );

    const isMyAd = Boolean(currentUser && owner?.user?.id === currentUser.id);
    const ownerLink = owner ? getOwnerLink(owner as Owner) : undefined;
    const phoneNumber = ad.assignedTo?.phoneNumber ?? owner?.phoneNumber;

    const contactInfo = useMemo(
        () => (
            <>
                <Title variant="h2">{t('ad:contactSeller')}</Title>
                <Box mt={2} display="flex" alignItems="center" gap="0.625rem">
                    <UserOrCompanyPreviewCore
                        user={
                            (ad.assignedTo || ad.owner?.user) && {
                                showActivity: true,
                                showCompany: true,
                                ...(ad.assignedTo ?? {
                                    ...owner,
                                    ...owner?.user,
                                    fullName: owner?.displayName,
                                }),
                            }
                        }
                        avatarProps={{ size: 56 }}
                        company={
                            ad.owner && !(ad.assignedTo || ad.owner.user)
                                ? ({
                                      ...owner?.company,
                                      image: owner?.image,
                                  } as unknown as Company)
                                : undefined
                        }
                        mainPublisher={ad.mainPublisher}
                        dataTestIdPrefix="contactBlock"
                        mainTypographyProps={{ sx: { fontSize: '1.125rem' } }}
                        secondaryTypographyProps={{ sx: { fontSize: '0.875rem' } }}
                    />
                    {ad.mainPublisher?.url && (
                        <IconButton href={ad.mainPublisher.url} target="_blank" size="small">
                            <Icon name="ExternalLink" size="1.2rem" />
                        </IconButton>
                    )}
                </Box>
                {phoneNumber && (
                    <HiddenPhoneNumberButton
                        phoneNumber={phoneNumber}
                        variant="outlined"
                        style={{ width: '100%', marginTop: '0.75rem' }}
                        data-target-id="contact-phone-number"
                    />
                )}
            </>
        ),
        [ad.assignedTo, ad.mainPublisher, ad.owner, owner, phoneNumber, t],
    );

    const messengerForm = useMemo(
        () =>
            isMyAd || ad.mainPublisher ? null : (
                <Box flex={isDesktop ? '1' : undefined}>
                    <Formik
                        initialValues={{
                            content: '',
                        }}
                        validationSchema={schema}
                        onSubmit={handleForm}
                    >
                        <Form
                            data-target-id="contact-form"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                            }}
                        >
                            {!sent && (
                                <TextField
                                    name="content"
                                    placeholder={t('ad:contactPlaceholder')}
                                    minRows={3}
                                    onClick={() => createAuthenticatedHandler()}
                                    style={isDesktop ? { marginTop: '0' } : undefined}
                                    multiline
                                />
                            )}
                            <Box mt={sent ? '1rem' : '0'}>
                                <Button
                                    type={!sent ? 'submit' : 'button'}
                                    size="large"
                                    startIcon={<Icon style={{ margin: '-0.25rem' }} name="ChatWhiteInactive" />}
                                    onClick={async () => {
                                        if (sent) {
                                            await router.push(routePathContacts);
                                        } else {
                                            logEventDynamic(AnalyticsEvent.SendMessage);
                                        }
                                    }}
                                    fullWidth
                                >
                                    {sent ? t('ad:accessMessaging') : t('ad:contactSubmit')}
                                </Button>
                            </Box>
                        </Form>
                    </Formik>
                </Box>
            ),
        [
            ad.mainPublisher,
            createAuthenticatedHandler,
            handleForm,
            isDesktop,
            isMyAd,
            logEventDynamic,
            router,
            schema,
            sent,
            t,
        ],
    );

    const publisherCTAs = useMemo(
        () =>
            !ad.mainPublisher ? null : (
                <Box marginTop={!isDesktop ? '0.8rem' : undefined}>
                    {ad.totalPublisher && ad.totalPublisher > 1 && (
                        <Button
                            data-target-id="contactBlock-seeOnOtherWebsite"
                            variant="outlined"
                            onClick={openPublishersModal}
                            fullWidth
                        >
                            {t('ad:seeOnOtherWebsites')}
                        </Button>
                    )}
                    {publishersModalProps.open && <PublishersModal {...publishersModalProps} adId={ad.id} />}
                </Box>
            ),
        [ad.id, ad.mainPublisher, ad.totalPublisher, publishersModalProps, isDesktop, openPublishersModal, t],
    );

    const contactPath = useMemo(() => {
        if (ad.assignedTo || ownerLink) {
            return ad.assignedTo ? `${routePathUser}/${ad.assignedTo.id}` : ownerLink;
        }
        return undefined;
    }, [ad.assignedTo, ownerLink]);

    return (
        <StyledContactBlock $isDesktop={isDesktop}>
            <Box
                onClick={contactPath ? () => router.push(contactPath) : undefined}
                style={{ cursor: contactPath ? 'pointer' : undefined }}
            >
                {contactInfo}
            </Box>
            {messengerForm}
            {publisherCTAs}
        </StyledContactBlock>
    );
};
export default ContactBlock;
